<template>
  <LightBox :display="display" @close="handleClose">
    <div class="bg-white text-sm rounded overflow-hidden">
      <div
        class="w-full border-b border-gray-5 text-xl font-bold flex items-center justify-between p-2 space-x-3"
      >
        <div
          class="flex items-center gap-2 text-black ml-2 w-full overflow-hidden @container"
        >
          <img :src="diagIcon" class="h-5 w-5" />
          <span class="overflow-hidden text-ellipsis whitespace-nowrap">{{
            $t('flowy.title')
          }}</span>
        </div>
        <BaseButtonOld
          color="white"
          variant="outline"
          @click="handleClose"
          class="flex-none"
        >
          {{ $t('flowy.close') }}
        </BaseButtonOld>
      </div>
      <div class="relative w-[98vw] h-full">
        <flow-chart
          v-if="!!rootNode && !loading"
          :focusNode="rootNode"
          :fetch-content="fetchContent"
        >
          <template v-slot="{ node }">
            <div>
              <div
                v-if="!hasEmptyBody(node.body)"
                class="bg-white rounded p-1 border border-gray-5 max-h-[144px] overflow-auto"
              >
                <div v-if="displayNode === node.id" v-click-outside="hideBody">
                  <content-viewer
                    :body="node.body"
                    :mentions="node.enrichedMentions"
                    @open-lightbox="configStore.setImageUrl"
                    @click:inline-content="onClickInlineContent"
                    @click:inline-content-modal="onClickInlineContent"
                  />
                  <BaseButtonOld
                    class="ml-auto"
                    variant="link"
                    size="xs"
                    @click="hideBody"
                  >
                    {{ $t('flowy.close') }}
                  </BaseButtonOld>
                </div>
                <button
                  v-else
                  class="hover:text-blue w-full group flex items-center gap-1"
                  @click="displayBody(node.id)"
                >
                  <div class="w-full">
                    {{
                      node.redirection
                        ? $t('flowy.redirection-preview')
                        : $t('flowy.node-preview')
                    }}
                  </div>

                  <FontAwesomeIcon
                    class="group-hover:text-gray-9 hover:!text-blue"
                    :icon="['fas', 'external-link-alt']"
                    @click.stop="getOnClickContent(node)"
                  />
                </button>
              </div>
              <div v-else>{{ $t('flowy.empty') }}</div>
            </div>
          </template>
        </flow-chart>
      </div>
    </div>
  </LightBox>
</template>

<script setup lang="ts">
import LightBox from './LightBox.vue';
import type { Pinia } from 'pinia';
import { inject } from 'vue';
const pinia = inject('pinia') as Pinia;
import { useKnowledgeStore } from '@getmayday/vue-modules';
import useEntities from '../../../composables/useEntities';

import diagIcon from '../../../assets/icons/diag-icon.svg';

const configStore = useConfigStore();
const knowledgeStore = useKnowledgeStore(pinia);

const { content, modalContent } = storeToRefs(knowledgeStore);

const { flowy } = storeToRefs(configStore);

const { goToEntity } = useEntities();

const display = ref(false);
const loading = ref(false);
const rootNode = ref();
const currentNode = ref();

const props = defineProps({
  isInModal: {
    type: Boolean,
    default: false,
  },
});

const handleClose = () => {
  display.value = false;
  displayNode.value = null;
  configStore.setFlowy(false);
};

onMounted(() => {
  configStore.setFlowy(false);
});

watch(flowy, async (newVal) => {
  if (newVal) {
    loading.value = true;
    currentNode.value = await knowledgeStore.getContentAncestors(
      props.isInModal ? modalContent.value.id : content.value.id,
    );
    if (currentNode.value.contentAncestors.length) {
      rootNode.value = await knowledgeStore.getContent(
        currentNode.value.contentAncestors[0].id,
      );
      rootNode.value.ancestors = currentNode.value.ancestors[0].split('/');
      rootNode.value.ancestors.push(currentNode.value.id);
    } else rootNode.value = currentNode.value;
    loading.value = false;
  }
  display.value = !!newVal;
});

const fetchContent = async (contentId: string): Promise<FlowyNode | null> => {
  const fetchedContent = await knowledgeStore.getContent(contentId);
  return fetchedContent || null;
};

// NODE BODY
const displayNode = ref<string | null>(null);

const displayBody = (nodeId: string) => {
  displayNode.value = nodeId;
};
const hideBody = ({ target }: { target: HTMLElement }) => {
  if (!target.classList.contains('select-none')) displayNode.value = null;
};

const hasEmptyBody = (body?: string) => {
  return (
    !body || body === '<p></p>' || body.match(/^<p data-uid="[^"]+"><\/p>$/)
  );
};

const onClickInlineContent = (targetContent: Entity) => {
  configStore.setFlowy(false);
  goToEntity({
    ...targetContent,
    deepLoading: true,
  });
};
const getOnClickContent = async (node: FlowyNode) => {
  let nodeToDisplay;
  if (node.redirection) {
    nodeToDisplay = await fetchContent(node.redirection.entityId);
  } else {
    nodeToDisplay = node;
  }

  if (nodeToDisplay) {
    onClickInlineContent(nodeToDisplay);
  }
};
</script>
